import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Dayjs } from "dayjs";
import { useMemo } from "react";

import { IPaginatedResponse } from "../../@types/PaginatedResponse";
import {
  IBillablePatient,
  IPatientDetailsDropdownItems,
  IPatientInfo,
  IPatientListVariants,
  IPatientMonitoringInfo,
  IPatientSearchListItem,
  IPortalPatientListItem,
  PatientAlertsTableContent,
} from "../../@types/Patient";
import { IUserPreferences } from "../../@types/Preferences";
import { DEFAULT_PAGE_SIZE } from "../../constants/pagination";
import { patientService } from "../../services/patientService";
import { userService } from "../../services/userService";
import { getLongFormattedDate } from "../../utils/dateFormatter";
import { ensureOk } from "../../utils/ensureOk";

function formatPatient(
  patient: IPortalPatientListItem
): IPortalPatientListItem {
  return {
    ...patient,
    signUpDate: patient.signUpDate
      ? getLongFormattedDate(patient.signUpDate)
      : "",
    lastUsed: patient.lastUsed ? getLongFormattedDate(patient.lastUsed) : null,
    surveyReceived: patient.surveyReceived
      ? getLongFormattedDate(patient.surveyReceived)
      : null,
  };
}

type UsePatientListOptions = {
  page?: number;
  pageSize: number;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  refetchOnMount?: "always" | boolean;
  // EN-5067: comma-separated custom-field ids for the enabled custom columns (undefined = none).
  customFieldIds?: string;
};

/**
 * Hook for the patient list with classic page-based pagination + server-side
 * sort. Each (page, pageSize, sortBy, sortDir) combination is its own cache
 * entry so jumping between pages is instant once visited and re-sorts trigger
 * a fresh fetch. Callers that want the full list (SearchBar, appointments)
 * pass `pageSize: PATIENT_LIST_PAGE_SIZE` to fetch everything in one request.
 * `pageSize` is required so a missing argument can't silently fetch 100k rows.
 */
export function usePatientList(
  { variant }: IPatientListVariants,
  {
    page = 1,
    pageSize,
    sortBy,
    sortDir,
    refetchOnMount = "always",
    customFieldIds,
  }: UsePatientListOptions
) {
  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: [
      `patient-list-${variant}`,
      variant,
      page,
      pageSize,
      sortBy ?? null,
      sortDir ?? null,
      customFieldIds ?? null,
    ],
    queryFn: async () => {
      const response = await patientService.getPatients({
        variant,
        page,
        pageSize,
        sortBy,
        sortDir,
        customFieldIds,
      });
      const { result, ...meta } = response.data;
      return { result: result.map(formatPatient), ...meta };
    },
    refetchOnMount,
    placeholderData: (previous) => previous,
  });

  const patients = useMemo(
    () => data?.result ?? ([] as IPortalPatientListItem[]),
    [data]
  );

  // Fallback for backends that don't (yet) return `totalCount` in the
  // paginated response: use the current page's row count so the header
  // count and single-page pagination still render something sensible.
  const totalCount =
    data?.totalCount ?? (data?.result ? data.result.length : 0);

  return {
    patients,
    totalCount,
    totalPages: data?.pages ?? (totalCount ? 1 : 0),
    currentPage: data?.currentPage ?? page,
    pageSize: data?.pageSize ?? pageSize,
    isLoading,
    isFetching,
    isError,
    refetch,
  };
}

export const PATIENT_SEARCH_LIST_QUERY_KEY = ["patient-search-list"] as const;

export function usePatientSearchList() {
  const { data, isLoading, isError, isRefetching, refetch } = useQuery<
    IPatientSearchListItem[]
  >({
    queryKey: PATIENT_SEARCH_LIST_QUERY_KEY,
    queryFn: async () => {
      const { data } = await patientService.getPatientSearchList();
      return data ?? [];
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return {
    patients: data ?? [],
    isLoading,
    isError,
    isRefetching,
    refetch,
  };
}

export function usePatientNavigationIds(
  {
    variant,
    sortBy,
    sortDir,
  }: IPatientListVariants & { sortBy?: string; sortDir?: "asc" | "desc" },
  enabled: boolean = true
) {
  const { data, isLoading, isError, refetch } = useQuery<number[]>({
    queryKey: [
      `patient-navigation-ids-${variant}`,
      variant,
      sortBy ?? null,
      sortDir ?? null,
    ],
    queryFn: async () => {
      const serverResponse = await patientService.getPatientNavigationIds({
        variant,
        sortBy,
        sortDir,
      });
      return serverResponse.data.patientIds;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - navigation IDs don't change often
  });

  return {
    patientIds: data ?? [],
    isLoading,
    isError,
    refetch,
  };
}

export function useBillablePatientList(
  fromDate: Dayjs,
  toDate: Dayjs,
  refetchOnMount: "always" | boolean = "always"
) {
  const fromDateString = fromDate.toISOString();
  const toDateString = toDate.toISOString();

  const { data, isLoading, isError, isRefetching, refetch } = useQuery<
    IBillablePatient[]
  >({
    queryKey: [`patient-list-billing`],
    queryFn: async () => {
      const serverResponse = await patientService.getBillablePatients(
        fromDateString,
        toDateString
      );

      const { data } = serverResponse;

      const billablePatientsParsed = data.map((patient: IBillablePatient) => {
        const signUpDate = patient?.signUpDate
          ? getLongFormattedDate(patient.signUpDate)
          : "";

        const lastUsed = patient?.lastUsed
          ? getLongFormattedDate(patient.lastUsed)
          : "";

        const dob = patient?.dob ? getLongFormattedDate(patient.dob) : "";

        const hospitalId = patient?.hospitalPatientId ?? "";

        return {
          pmpId: patient.pmpId,
          name: patient.name,
          signUpDate,
          dob,
          lastUsed,
          hospitalPatientId: hospitalId,
          numberOfBillableDays: patient.numberOfBillableDays,
        };
      });

      return billablePatientsParsed as IBillablePatient[];
    },
    refetchOnMount,
  });

  return {
    patients: data,
    isLoading,
    isError,
    isRefetching,
    refetch,
  };
}

export function usePatientInfo({
  patientId,
}: {
  patientId: string | undefined;
}) {
  const { data, isFetching, isError, isRefetching, refetch } =
    useQuery<IPatientInfo>({
      queryKey: [`patient-info-${patientId}`, patientId],
      queryFn: async () => {
        if (!patientId) return { condition: "" } as unknown as IPatientInfo;

        const { data } = await patientService.getPatientById(patientId);
        const { lastUsed } = data;

        const patientConditions: string[] = [];

        data?.conditions.forEach((condition: any) => {
          patientConditions.push(condition.name);
        });

        return {
          id: data?.id,
          birthDate: data?.dob,
          flagged: data?.flagged,
          archived: data?.archived ?? false,
          conditionId: data.conditions[0].typeId,
          hospitalId: data?.hospitalId,
          hospitalPatientId: data?.hospitalPatientId,
          gender: data?.gender,
          ethnicity: data?.ethnicity,
          user: {
            id: data?.userId,
            name: `${data?.firstName} ${data?.lastName}`,
            firstName: data?.firstName,
            lastName: data?.lastName,
            email: data?.email,
            phoneNumber: data?.phoneNumber,
            lastUsed: lastUsed ? getLongFormattedDate(lastUsed) : null,
            weight: data?.weight,
            height: data?.height,
            country: data?.country,
            caregiverName: data?.caregiverName,
            caregiverPhoneNumber: data?.caregiverPhoneNumber,
          },
          ppsn: data?.ppsn,
          ihi: data?.ihi,
          county: data?.county,
          nextOfKinName: data?.nextOfKinName,
          nextOfKinEmail: data?.nextOfKinEmail,
          contactPreference: data?.contactPreference,
          gp: data?.gp,
          referralSource: data?.referralSource,
          address: data?.address,
          allergies: data?.allergies ?? data?.Allergies,
          diagnoses: data?.diagnoses ?? data?.Diagnoses,
          appVersion: data?.appVersion,
          ishltBaselineActive: data?.ishltBaselineActive,
        } as IPatientInfo;
      },
    });

  return {
    patient: data as IPatientInfo,
    isFetching,
    isError,
    refetchPatientInfo: refetch,
    isRefetching,
  };
}

export function usePatientMonitoringInfo({
  patientId,
}: {
  patientId: string | undefined;
}) {
  const { data, isFetching, isError, refetch } =
    useQuery<IPatientMonitoringInfo>({
      queryKey: [`patient-monitoring-info-${patientId}`, patientId],
      queryFn: async () => {
        if (!patientId) return {} as unknown as IPatientMonitoringInfo;

        const { data } = await patientService.getPatientMonitoringInfo(
          patientId
        );

        return {
          hospitalName: data?.hospitalName,
          latestReviewedBy: data?.latestReviewedBy,
          latestReview: getLongFormattedDate(data?.latestReview) || null,
          goals: data?.goals,
        } as IPatientMonitoringInfo;
      },
    });

  return {
    patientMonitoringInfo: data as IPatientMonitoringInfo,
    isFetching,
    isError,
    refetchMonitoringInfo: refetch,
  };
}

export async function resolveTrigger(
  idList: string[],
  patientId: number,
  outcome?: any,
  note?: string
) {
  const { data, status } = await patientService.resolveTriggerAlert(
    idList,
    patientId,
    outcome,
    note
  );
  return { success: status >= 200 && status < 300, data };
}

export function usePatientDetailsDropdownItems({
  patientId,
}: {
  patientId: string | undefined;
}) {
  const { data, isFetching, isError, refetch } =
    useQuery<IPatientDetailsDropdownItems>({
      queryKey: [`patient-details-dropdown-items-${patientId}`, patientId],
      queryFn: async () => {
        const { data } = await patientService.getPatientDetailsDropDownItems();

        return {
          countryItems: data?.countryItems,
          conditionItems: data?.conditionItems,
          hospitalItems: data?.hospitalItems,
          countyItems: data?.countyItems,
          contactPreferenceItems: data?.contactPreferenceItems,
          referralSourceItems: data?.referralSourceItems,
        } as IPatientDetailsDropdownItems;
      },
    });

  const queryClient = useQueryClient();

  const cancelQuery = () => {
    queryClient.cancelQueries({
      queryKey: [`patient-details-dropdown-items-${patientId}`, patientId],
    });
  };

  return {
    dropdownItems: data as IPatientDetailsDropdownItems,
    isFetching,
    isError,
    refetch,
    cancelQuery,
  };
}

export function useUserPreferencesData() {
  const { data, isFetching, isError, refetch, isRefetching } =
    useQuery<IUserPreferences>({
      queryKey: [`user-preferences`],
      queryFn: async () => {
        const { data } = await userService.getUserPreferences();

        return {
          bloodGlucoseUnit: data.bloodGlucoseUnit,
          created: data.created,
          creatinineUnit: data.creatinineUnit,
          distanceUnit: data.distanceUnit,
          fitnessDataSource: data.fitnessDataSource,
          flowRateUnit: data.flowRateUnit,
          fluidsDashboard: data.fluidsDashboard,
          heightUnit: data.heightUnit,
          id: data.id,
          liquidUnit: data.liquidUnit,
          modified: data.modified,
          order: data.order,
          phosphateUnit: data.phosphateUnit,
          publish: data.publish,
          temperatureUnit: data.temperatureUnit,
          uid: data.uid,
          userId: data.userId,
          weightUnit: data.weightUnit,
          dataDisplayPreference: data.dataDisplayPreference,
          patientDetailsCardsOrder: data.patientDetailsCardsOrder,
        } as IUserPreferences;
      },
    });

  return {
    userPreferences: data as IUserPreferences,
    isFetching,
    isError,
    isRefetching,
    refetch,
  };
}

type AlertsPaginatedResponse = IPaginatedResponse<PatientAlertsTableContent[]>;

/**
 * Hook for fetching patient alerts with infinite scrolling support.
 * Uses TanStack Query's useInfiniteQuery for paginated data fetching.
 *
 * @param patientId - The patient ID to fetch alerts for
 * @param pageSize - Number of items per page (default: 20)
 */
export function useInfiniteAlerts(
  patientId: string | number,
  pageSize: number = DEFAULT_PAGE_SIZE
) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isRefetching,
  } = useInfiniteQuery({
    queryKey: ["patient-alerts-infinite", patientId, pageSize],
    queryFn: async ({ pageParam }): Promise<AlertsPaginatedResponse> => {
      if (!patientId) {
        return {
          result: [],
          currentPage: 1,
          pageSize,
          pages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        };
      }

      const response = await patientService.getPatientAlertsPaginated(
        patientId,
        pageParam,
        pageSize
      );

      return response.data as AlertsPaginatedResponse;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: AlertsPaginatedResponse) => {
      if (lastPage.hasNextPage) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
  });

  // Flatten all pages into a single array of alerts
  const alerts =
    data?.pages.flatMap((page: AlertsPaginatedResponse) => {
      const items = page.result || [];
      return items.map((alertData: PatientAlertsTableContent) => {
        return {
          ...alertData,
        } as PatientAlertsTableContent;
      });
    }) || [];

  // Get total count from the first page
  const totalCount =
    data?.pages[0]?.pages !== undefined
      ? data.pages[0].pages * pageSize
      : alerts.length;

  return {
    alerts,
    totalCount,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isRefetching,
  };
}

// EN-4960: the patient's full ward set (and primary), backing the side-panel chips.
export function usePatientWards(patientId: number | undefined) {
  const { data, isFetching, isError, isSuccess, isFetched, refetch } = useQuery(
    {
      queryKey: ["patient-wards", patientId],
      // EN-5268: the interceptor resolves 4xx/5xx, so without ensureOk an error body counts as
      // success and the ward editor opens from the primary-only fallback.
      queryFn: async () =>
        ensureOk(await patientService.getPatientWards(patientId as number)),
      enabled: !!patientId,
    }
  );

  return {
    patientWards: data,
    isPatientWardsFetching: isFetching,
    isPatientWardsError: isError,
    isPatientWardsSuccess: isSuccess,
    // True once the query has settled at least once (success OR error).
    isPatientWardsFetched: isFetched,
    refetchPatientWards: refetch,
  };
}
